import { useEffect } from 'react';
import { isTypingInInteractiveElement } from './useSecretAppShortcuts';

type ImpersonationShortcutEvent = Pick<
  KeyboardEvent,
  'key' | 'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey' | 'target'
>;

/**
 * Resolves the global impersonation chord. Route ownership is handled by the
 * caller's `enabled` gate; when the palette is open, its focused search input
 * is allowed so the same chord can toggle it closed.
 */
export function resolveGlobalImpersonationShortcut(
  event: ImpersonationShortcutEvent,
  paletteOpen = false,
): boolean {
  if (!(event.metaKey || event.ctrlKey) || event.shiftKey || event.altKey) return false;
  if (event.key.toLowerCase() !== 'i') return false;
  if (!paletteOpen && isTypingInInteractiveElement(event.target)) return false;
  return true;
}

export function useGlobalImpersonationShortcut({
  enabled,
  open,
  onToggle,
}: {
  enabled: boolean;
  open: boolean;
  onToggle: () => void;
}): void {
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!resolveGlobalImpersonationShortcut(event, open)) return;
      event.preventDefault();
      onToggle();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [enabled, onToggle, open]);
}
